import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type { FlowDto, FlowSlotDto } from '@/types/dtos/flows/response/flow.dto';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type {
  FlowMediaPlaylistsDto,
  MediaPlaylistDto,
  SessionMediaPlaylistsDto,
} from '@/types/dtos/media/media-playlist.dto';
import type { NonSlotFlowPlaylistScope } from '@/types/dtos/media/flow-playlist-scope.enum';
import { NON_SLOT_FLOW_PLAYLIST_SCOPES } from '@/types/dtos/media/flow-playlist-scope.enum';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import type { GetSessionsDto } from '@/types/dtos/sessions/response/get-sessions.dto';
import { getObjectId } from '@/helpers/object.id.helper';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { GET_EVENT_FLOWS_QUERY_KEY } from '@/state/queries/flows/use-get-event-flows';
import { GET_SESSION_QUERY_KEY } from '@/state/queries/sessions/use-get-session';
import { GET_EVENT_SESSIONS_QUERY_KEY } from '@/state/queries/sessions/use-event-sessions';
import { useFlowStore } from '@/state/stores/use-flow-store';
import { useEventStore } from '@/state/stores/use-event-store';
import { hydratePlaylistRefs, upsertMediaInList } from '@/state/mutations/media/patch-media-playlist-cache';

export type CreatedMediaPatch = {
  media: MediaDto;
  parentId: string;
  parentType: string;
  playlistScope?: NonSlotFlowPlaylistScope;
  placement?: { id: string; type: 'Slot' };
};

type EventSessionsPage =
  | GetSessionsDto
  | GetSessionsDto[]
  | [GetSessionsDto | GetSessionsDto[], GetSessionsDto | GetSessionsDto[]];

function sessionMatchesParent(session: SessionDto, parentId: string): boolean {
  const origin = session.origin ? getObjectId(session.origin) : session.id;
  return session.id === parentId || origin === parentId;
}

function flowMatchesParent(flow: FlowDto, parentId: string): boolean {
  const origin = flow.origin ? getObjectId(flow.origin) : flow.id;
  return flow.id === parentId || origin === parentId;
}

function appendCreatedMediaToPlaylist(
  playlist: MediaPlaylistDto | undefined,
  media: MediaDto,
  scope: string,
  slotId?: string,
): MediaPlaylistDto | undefined {
  if (!playlist) {
    return playlist;
  }

  const hydrated = hydratePlaylistRefs(playlist, [
    media,
    ...(playlist.refs.map((ref) => ref.media).filter(Boolean) as MediaDto[]),
  ]);
  if (hydrated.refs.some((ref) => ref.mediaId === media.id)) {
    return hydratePlaylistRefs(hydrated, [media]);
  }

  const maxSequence = hydrated.refs.reduce((max, ref) => Math.max(max, ref.sequence), 0);
  return {
    ...hydrated,
    scope: hydrated.scope || scope,
    ...(slotId ? { slotId: hydrated.slotId ?? slotId } : {}),
    refs: [
      ...hydrated.refs,
      {
        usageId: `pending:${media.id}`,
        mediaId: media.id,
        sequence: maxSequence + 1,
        media,
      },
    ],
  };
}

function patchSessionWithCreatedMedia(session: SessionDto, patch: CreatedMediaPatch): SessionDto {
  if (!sessionMatchesParent(session, patch.parentId)) {
    return session;
  }

  const nextMedia = upsertMediaInList(session.media, patch.media);
  const existingPlaylist = session.mediaPlaylists?.session;
  const nextPlaylist = appendCreatedMediaToPlaylist(existingPlaylist, patch.media, 'session');

  if (nextMedia === session.media && nextPlaylist === existingPlaylist) {
    return session;
  }

  const mediaPlaylists: SessionMediaPlaylistsDto | undefined = nextPlaylist
    ? { session: nextPlaylist }
    : session.mediaPlaylists;

  return {
    ...session,
    media: nextMedia,
    ...(mediaPlaylists ? { mediaPlaylists } : {}),
  };
}

function patchFlowPlaylistsWithCreatedMedia(
  mediaPlaylists: FlowMediaPlaylistsDto | undefined,
  patch: CreatedMediaPatch,
): FlowMediaPlaylistsDto | undefined {
  if (!mediaPlaylists) {
    return mediaPlaylists;
  }

  const next: FlowMediaPlaylistsDto = { ...mediaPlaylists };
  let changed = false;

  if (patch.placement?.type === 'Slot' && patch.placement.id) {
    const slotId = patch.placement.id;
    const existing = next.slots?.[slotId];
    const updated = appendCreatedMediaToPlaylist(existing, patch.media, 'slot', slotId);
    if (updated && updated !== existing) {
      next.slots = { ...(next.slots ?? {}), [slotId]: updated };
      changed = true;
    }
  } else if (patch.playlistScope && NON_SLOT_FLOW_PLAYLIST_SCOPES.includes(patch.playlistScope)) {
    const scope = patch.playlistScope;
    const existing = next[scope];
    const updated = appendCreatedMediaToPlaylist(existing, patch.media, scope);
    if (updated && updated !== existing) {
      next[scope] = updated;
      changed = true;
    }
  }

  return changed ? next : mediaPlaylists;
}

function patchFlowWithCreatedMedia(flow: FlowDto, patch: CreatedMediaPatch): FlowDto {
  const parentType = patch.parentType.toLowerCase();

  if (parentType === 'flow') {
    if (!flowMatchesParent(flow, patch.parentId)) {
      return flow;
    }

    const nextMedia = upsertMediaInList(flow.media, patch.media);
    const nextPlaylists = patchFlowPlaylistsWithCreatedMedia(flow.mediaPlaylists, patch);
    if (nextMedia === flow.media && nextPlaylists === flow.mediaPlaylists) {
      return flow;
    }
    return {
      ...flow,
      media: nextMedia,
      ...(nextPlaylists ? { mediaPlaylists: nextPlaylists } : {}),
    };
  }

  if (parentType !== 'session' || !flow.slots?.length) {
    return flow;
  }

  let changed = false;
  const slots: FlowSlotDto[] = flow.slots.map((slot): FlowSlotDto => {
    if (typeof slot.content !== 'object' || slot.content == null || !('id' in slot.content)) {
      return slot;
    }
    const session = slot.content as SessionDto;
    const patched = patchSessionWithCreatedMedia(session, patch);
    if (patched === session) {
      return slot;
    }
    changed = true;
    return { ...slot, content: patched };
  });

  return changed ? { ...flow, slots } : flow;
}

function patchFlowQueryData(data: FlowDto | FlowDto[], patch: CreatedMediaPatch): FlowDto | FlowDto[] {
  if (Array.isArray(data)) {
    return data.map((flow) => patchFlowWithCreatedMedia(flow, patch));
  }
  return patchFlowWithCreatedMedia(data, patch);
}

function patchGetSessionsPage(page: GetSessionsDto, patch: CreatedMediaPatch): GetSessionsDto {
  return {
    ...page,
    data: page.data?.map((session) => patchSessionWithCreatedMedia(session, patch)) ?? page.data,
  };
}

function patchEventSessionsPage(page: EventSessionsPage, patch: CreatedMediaPatch): EventSessionsPage {
  if (Array.isArray(page)) {
    return page.map((entry) => {
      if (Array.isArray(entry)) {
        return entry.map((nested) => patchGetSessionsPage(nested, patch));
      }
      return patchGetSessionsPage(entry, patch);
    }) as EventSessionsPage;
  }
  return patchGetSessionsPage(page, patch);
}

function patchEventSessionsQueryData(data: unknown, patch: CreatedMediaPatch): unknown {
  if (!data || typeof data !== 'object') {
    return data;
  }

  if ('pages' in data && Array.isArray((data as InfiniteData<EventSessionsPage>).pages)) {
    const infinite = data as InfiniteData<EventSessionsPage>;
    return {
      ...infinite,
      pages: infinite.pages.map((page) => patchEventSessionsPage(page, patch)),
    };
  }

  if (Array.isArray(data)) {
    return (data as SessionDto[]).map((session) => patchSessionWithCreatedMedia(session, patch));
  }

  return data;
}

function patchEventStoreWithCreatedMedia(patch: CreatedMediaPatch) {
  if (patch.parentType.toLowerCase() !== 'session') {
    return;
  }

  useEventStore.setState((state) => {
    const patchSessions = (sessions?: SessionDto[]) => {
      if (!sessions?.length) {
        return sessions;
      }
      let changed = false;
      const next = sessions.map((session) => {
        const patched = patchSessionWithCreatedMedia(session, patch);
        if (patched !== session) {
          changed = true;
        }
        return patched;
      });
      return changed ? next : sessions;
    };

    const eventSessionsPlan = patchSessions(state.eventSessionsPlan);
    const eventSessionsOrganize = patchSessions(state.eventSessionsOrganize);
    if (eventSessionsPlan === state.eventSessionsPlan && eventSessionsOrganize === state.eventSessionsOrganize) {
      return state;
    }

    return {
      eventSessionsPlan,
      eventSessionsOrganize,
      eventSessions: eventSessionsPlan ?? state.eventSessions,
    };
  });
}

function patchFlowStoreWithCreatedMedia(patch: CreatedMediaPatch) {
  useFlowStore.setState((state) => {
    const patchFlow = (flow?: FlowDto) => (flow ? patchFlowWithCreatedMedia(flow, patch) : flow);
    const flow = patchFlow(state.flow);
    const flowPlan = patchFlow(state.flowPlan);
    const flowOrganize = patchFlow(state.flowOrganize);

    if (flow === state.flow && flowPlan === state.flowPlan && flowOrganize === state.flowOrganize) {
      return state;
    }

    return { flow, flowPlan, flowOrganize };
  });
}

/**
 * Immediately show newly created media in Assets lists.
 * Playlist WS updates are lean (no embedded media); without this, new refs are filtered out until reload.
 */
export function patchCreatedMediaInQueryCache(queryClient: QueryClient, patch: CreatedMediaPatch) {
  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto | FlowDto[]>({
    queryKey: [GET_FLOW_QUERY_KEY],
  })) {
    if (!data) continue;
    queryClient.setQueryData(queryKey, patchFlowQueryData(data, patch));
  }

  for (const [queryKey, data] of queryClient.getQueriesData<FlowDto[]>({
    queryKey: [GET_EVENT_FLOWS_QUERY_KEY],
  })) {
    if (!data || !Array.isArray(data)) continue;
    queryClient.setQueryData(
      queryKey,
      data.map((flow) => patchFlowWithCreatedMedia(flow, patch)),
    );
  }

  if (patch.parentType.toLowerCase() === 'session') {
    for (const [queryKey, data] of queryClient.getQueriesData<SessionDto>({
      queryKey: [GET_SESSION_QUERY_KEY],
    })) {
      if (!data) continue;
      queryClient.setQueryData(queryKey, patchSessionWithCreatedMedia(data, patch));
    }

    for (const [queryKey, data] of queryClient.getQueriesData({
      queryKey: [GET_EVENT_SESSIONS_QUERY_KEY],
    })) {
      if (!data) continue;
      queryClient.setQueryData(queryKey, patchEventSessionsQueryData(data, patch));
    }
  }

  patchFlowStoreWithCreatedMedia(patch);
  patchEventStoreWithCreatedMedia(patch);
}
